import React, { createContext, useContext, useMemo } from 'react';
import {
  useRealtimeBalance,
  useRealtimeDeposits,
  useRealtimeWithdrawals,
  useRealtimeKYC,
  useRealtimeTrades,
  useRealtimeNotifications
} from '../hooks/useRealtimeData';

const RealtimeContext = createContext();

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within RealtimeProvider');
  }
  return context;
};

export const RealtimeProvider = ({ children, userId }) => {
  // Real-time data hooks
  const balanceData = useRealtimeBalance(userId);
  const depositsData = useRealtimeDeposits(userId);
  const withdrawalsData = useRealtimeWithdrawals(userId);
  const kycData = useRealtimeKYC(userId);
  const tradesData = useRealtimeTrades(userId);
  const notificationsData = useRealtimeNotifications(userId);

  // Memoize connection status
  const isConnected = useMemo(() => !!userId, [userId]);
  const lastUpdate = useMemo(() => new Date(), [balanceData.balance, depositsData.deposits, withdrawalsData.withdrawals]);

  const value = useMemo(() => ({
    // Balance
    balance: balanceData.balance,
    balanceLoading: balanceData.loading,
    
    // Deposits
    deposits: depositsData.deposits,
    depositsLoading: depositsData.loading,
    
    // Withdrawals
    withdrawals: withdrawalsData.withdrawals,
    withdrawalsLoading: withdrawalsData.loading,
    
    // KYC
    kycStatus: kycData.kycStatus,
    kycLevel: kycData.kycLevel,
    kycLoading: kycData.loading,
    
    // Trades
    trades: tradesData.trades,
    tradesLoading: tradesData.loading,
    
    // Notifications
    notifications: notificationsData.notifications,
    unreadCount: notificationsData.unreadCount,
    
    // Connection status
    isConnected,
    lastUpdate
  }), [
    balanceData.balance,
    balanceData.loading,
    depositsData.deposits,
    depositsData.loading,
    withdrawalsData.withdrawals,
    withdrawalsData.loading,
    kycData.kycStatus,
    kycData.kycLevel,
    kycData.loading,
    tradesData.trades,
    tradesData.loading,
    notificationsData.notifications,
    notificationsData.unreadCount,
    isConnected,
    lastUpdate
  ]);

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};
